import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress,
  Paper,
  Container,
  Alert,
  AlertTitle,
} from '@mui/material';
import { 
  Check, 
  AlertCircle,
  Home as HomeIcon,
  ArrowRight,
  Receipt,
  CreditCard
} from 'lucide-react';
import { trackPaid } from '../../../utils/analytics';

const PaymentSuccessContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAccessTokenSilently } = useAuth0();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const paymentIntent = searchParams.get('payment_intent');
        const redirectStatus = searchParams.get('redirect_status');

        console.log('URL Parameters:', {
          paymentIntent,
          redirectStatus
        });

        if (!paymentIntent) {
          throw new Error('No payment intent ID found in URL');
        }

        if (redirectStatus !== 'succeeded') {
          throw new Error(`Payment was not successful. Status: ${redirectStatus}`);
        }

        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'openid profile email'
        });

        console.log('Making verification request...');
        
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/payments/verify/${paymentIntent}/`, 
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Payment verification failed:', {
            status: response.status,
            statusText: response.statusText,
            error: errorData
          });
          throw new Error(errorData.message || 'Failed to verify payment');
        }

        const data = await response.json();
        console.log('Payment verification response:', data);

        if (!data) {
          throw new Error('No data received from verification endpoint');
        }

        if (data.order_status === 'refunded' || data.order_status === 'refund_pending') {
          // money-path-fixes item 1, Fix 1a (D-005): this order lost the
          // last-seat race. 'refunded' means the Stripe refund already
          // happened; 'refund_pending' means it hasn't yet (waiting on
          // refund_seat_taken_orders). Either way, show the server's
          // message, not the generic success screen -- checking order_status
          // here, not the raw payment_intent `status` (which reads
          // 'succeeded' in both cases, since the charge itself went
          // through) -- and never fire the `paid` event for a loser.
          console.log('Payment refunded or refund pending (last-seat race):', data);
          setPaymentDetails(data);
        } else if (data.status === 'succeeded' || data.status === 'paid' || data.status === 'success' || data.order_status === 'paid') {
          console.log('Payment verified successfully:', data);
          trackPaid(data.order_id || paymentIntent, data.amount);
          setPaymentDetails(data);
        } else {
          console.error('Invalid payment status:', data.status);
          throw new Error('Payment verification failed: Invalid status');
        }

      } catch (err) {
        console.error('Payment verification error:', err);
        setError(err.message || 'An error occurred while verifying your payment');
      } finally {
        setIsLoading(false);
      }
    };

    verifyPayment();
  }, [searchParams, getAccessTokenSilently]);

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="calc(100vh - 200px)"
          gap={2}
        >
          <CircularProgress size={40} sx={{ color: '#FF4775' }} />
          <Typography variant="body1" color="text.secondary">
            Verifying your payment...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !paymentDetails) {
    return (
      <Container maxWidth="md">
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="calc(100vh - 200px)"
          padding={3}
        >
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              borderRadius: 2,
              width: '100%',
              maxWidth: 600,
              textAlign: 'center'
            }}
          >
            <Box 
              sx={{ 
                width: 60, 
                height: 60, 
                borderRadius: '50%', 
                bgcolor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}
            >
              <AlertCircle size={30} color="#EF4444" />
            </Box>

            <Typography variant="h4" gutterBottom fontWeight={600}>
              Payment Verification Failed
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {error || 'There was an issue verifying your payment. Please contact support if this persists.'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button 
                variant="outlined"
                onClick={() => navigate('/payment')}
                startIcon={<CreditCard />}
                sx={{
                  borderColor: '#FF4775',
                  color: '#FF4775',
                  '&:hover': {
                    borderColor: '#FF385C',
                    bgcolor: 'rgba(255, 71, 117, 0.04)'
                  }
                }}
              >
                Try Again
              </Button>

              <Button 
                variant="contained"
                onClick={() => navigate('/')}
                startIcon={<HomeIcon />}
                sx={{ 
                  bgcolor: '#FF4775',
                  '&:hover': {
                    bgcolor: '#FF385C'
                  }
                }}
              >
                Go Home
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Fix 1a (D-018 partial): 'refund_pending' is the honest in-between state
  // (D-005) -- the Stripe refund itself hasn't succeeded yet -- and reads
  // the same as 'refunded' for this screen's heading/CTA treatment until
  // the full screen is ported at Pass 3 (see docs/behaviors register).
  const isSeatTakenState =
    paymentDetails &&
    (paymentDetails.order_status === 'refunded' || paymentDetails.order_status === 'refund_pending');

  return (
    <Container maxWidth="md">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="calc(100vh - 200px)"
        padding={3}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
            maxWidth: 600,
            width: '100%'
          }}
        >
          {/* money-path-fixes item 1: a last-seat-race loser (refunded or
              refund_pending) must not see the success headline; only the
              heading + server message below. */}
          {!isSeatTakenState && (
            <>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: '#DCFCE7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}
              >
                <Check size={30} color="#10B981" />
              </Box>

              <Typography variant="h4" gutterBottom fontWeight={600}>
                Payment Successful!
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Thank you for your order. Your order details are below and in My Profile.
              </Typography>
            </>
          )}

          {isSeatTakenState && (
            // Fix 1a (D-018 partial): a sentence-case heading naming the
            // amount, honest for whichever state the server actually
            // returned -- "Refunded" only once the refund has happened,
            // "Refunding" while it's still in flight (order_status
            // 'refund_pending', D-005). The Alert below is unchanged
            // (colour/layout choices wait for the Pass 3 port, D-018 rest).
            <>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                {`${paymentDetails.order_status === 'refunded' ? 'Refunded' : 'Refunding'} $${(paymentDetails.amount || 0).toFixed(2)}`}
              </Typography>
              <Alert severity="warning" sx={{ mb: 4, textAlign: 'left' }}>
                <Typography variant="body2">
                  {paymentDetails.message}
                </Typography>
              </Alert>
            </>
          )}

          {paymentDetails && !isSeatTakenState && (
            <Alert severity="success" sx={{ mb: 4, textAlign: 'left' }}>
              <AlertTitle>Order Details</AlertTitle>
              <Typography variant="body2">
                Order Number: {paymentDetails.order_number}
              </Typography>
              <Typography variant="body2">
                Amount Paid: ${(paymentDetails.amount || 0).toFixed(2)}
              </Typography>
              {paymentDetails.subscription_type && (
                <Typography variant="body2">
                  Plan: {paymentDetails.subscription_type}
                </Typography>
              )}
              {paymentDetails.shipping_method && (
                <Typography variant="body2">
                  Delivery Method: {paymentDetails.shipping_method}
                </Typography>
              )}
            </Alert>
          )}

          <Box sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Fix 1a (D-018 partial): the loser has no order to view --
                only the circle-browsing CTA in this state. */}
            {!isSeatTakenState && (
              <Button
                variant="outlined"
                onClick={() => navigate('/my-profile')}
                startIcon={<Receipt />}
                sx={{
                  borderColor: '#FF4775',
                  color: '#FF4775',
                  '&:hover': {
                    borderColor: '#FF385C',
                    bgcolor: 'rgba(255, 71, 117, 0.04)'
                  }
                }}
              >
                View Order Details
              </Button>
            )}

            <Button
              variant="contained"
              onClick={() => navigate('/all-circles')}
              endIcon={<ArrowRight />}
              sx={{
                bgcolor: '#FF4775',
                '&:hover': {
                  bgcolor: '#FF385C'
                }
              }}
            >
              Explore More Circles
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default PaymentSuccessContent;